import clsx from 'clsx';
import React, {FC, ReactNode} from 'react';

interface PageHeroProps {
  /** 右侧视觉主体（variant='light' 时渲染为右栏；居中布局时忽略） */
  aside?: ReactNode;
  /** CTA 按钮组（建议用 v3/classes 的按钮 token） */
  actions?: ReactNode;
  /** light 变体的布局：left=左文右图两栏（默认），center=居中单栏 */
  align?: 'center' | 'left';
  /** 顶部小徽章（light、soft 两种变体均渲染，如「最后更新 · 2026-08」） */
  badge?: ReactNode;
  description?: ReactNode;
  title: ReactNode;
  /** light=首页同款浅色渐变（营销页），soft=浅色精修头部（文档/政策页） */
  variant: 'light' | 'soft';
}

/**
 * v3 页面 hero。两个变体对应首页家族的两种头部：
 * - light：gradient-hero + grid-bg + noise-overlay，与首页 Hero 同款底；
 *   注意内容层必须带 z-10——noise-overlay 的 ::before 是 z-index:1 的全幅伪元素，
 *   z-auto 的内容会被它盖住（首页 Hero 同款约定，勿删）。
 * - soft：白底自上而下的极淡 indigo 渐变，居中标题，供文档/政策页使用
 *   （法律文档不配大视觉 hero，避免喧宾夺主）。
 */
const PageHero: FC<PageHeroProps> = ({align = 'left', aside, actions, badge, description, title, variant}) => {
  if (variant === 'soft') {
    return (
      <section className="relative overflow-hidden bg-gradient-to-b from-indigo-50 via-indigo-50/40 to-white">
        <div className="max-w-[90rem] mx-auto px-6 lg:px-8 pt-14 pb-10 lg:pt-20 lg:pb-14 text-center">
          {badge && (
            <div className="chip-solid inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm text-slate-500 mb-6">
              {badge}
            </div>
          )}
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-900 tracking-tight leading-[1.15] mb-4">
            {title}
          </h1>
          {description && <p className="text-slate-500 max-w-2xl mx-auto lg:text-lg leading-relaxed">{description}</p>}
          {actions && <div className="flex flex-wrap justify-center gap-4 mt-8">{actions}</div>}
        </div>
      </section>
    );
  }

  // light 变体：有 aside 且 align=left 时两栏，否则居中单栏
  const centered = align === 'center' || !aside;
  return (
    <section className="relative overflow-hidden gradient-hero grid-bg noise-overlay">
      <div
        className={clsx(
          'relative z-10 max-w-[90rem] mx-auto px-6 lg:px-8 py-16 lg:py-24',
          centered ? undefined : 'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center',
        )}
      >
        <div className={clsx('space-y-5', centered && 'max-w-5xl mx-auto text-center')}>
          {badge && (
            <div className="chip-solid inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm text-slate-500">
              {badge}
            </div>
          )}
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight leading-[1.1]">
            {title}
          </h1>
          {description && (
            <p className={clsx('text-slate-500 lg:text-lg leading-relaxed max-w-xl', centered && 'mx-auto')}>
              {description}
            </p>
          )}
          {actions && <div className={clsx('flex flex-wrap gap-4 pt-3', centered && 'justify-center')}>{actions}</div>}
        </div>
        {aside && !centered && <div className="relative">{aside}</div>}
      </div>
    </section>
  );
};

export default PageHero;
